import { Injectable } from '@angular/core';
import { FieldsService } from './fields.service';
import { TaxonomyService } from './taxonomy.service';
import { TaxonomyOverridesService } from './taxonomy-overrides.service';
import { AuthRESTService } from './auth-rest.service';
import { AuthService } from './auth.service';
import { MenuRESTService } from './menu-rest.service';
import { Router } from '@angular/router';
import { LogRocketService } from './log-rocket.service';
import { AppVersionService } from './app-version.service';
import { LanguageService } from './language.service';
import { PackagesService } from './packages.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class NgInitDataService {
  constructor(
    public _taxonomyService: TaxonomyService,
    public _taxonomyOverridesService: TaxonomyOverridesService,
    public _fieldsService: FieldsService,
    private _authRESTService: AuthRESTService,
    private _authService: AuthService,
    private _menuRESTService: MenuRESTService,
    private _router: Router,
    private _logRocketService: LogRocketService,
    private _appVersionService: AppVersionService,
    private _languageService: LanguageService,
    private _packagesService: PackagesService
  ) { }

  /**
   * Routes that serve anonymous visitors and must never hit login: the
   * website booking widget, and the gift certificate balance page a
   * recipient reaches from a texted or emailed link with no session.
   */
  static readonly PUBLIC_PATHS = ['/widgets/booking/', '/gift-certificate-balance'];

  init(): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      // Public pages (the website booking widget, the gift certificate
      // balance page) have no session and need none of the staff bootstrap
      // data; skip the login probe entirely so the visitor is never
      // redirected to /auth/login.
      if (NgInitDataService.PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p))) {
        this._authService.set_user_authenticated(false);
        resolve();
        return;
      }

      this._authRESTService.test_login().subscribe(response => {

        // First check if user is authenticated
        if (response['success'] == true) {
          // Set the current user and authentication state
          this._authService.current_user = response['current_user'];
          this._authService.csrf_token = response['csrf_token'];
          this._authService.logout_token = response['logout_token'];
          this._authService.studios = response['studios'];

          // Apply the user's saved language from their Drupal account.
          this._languageService.applyUserLanguage();

          // Check if user is a franchisee who needs to agree to the license
          const userRoles = this._authService.current_user?.['roles'] || [];
          const isFranchisee = userRoles.includes('franchisee');
          const licenseAgreed = response['current_user']?.['field_license_agreed'] &&
                                response['current_user']?.['field_license_agreed'] !== '0';

          // Only enforce license agreement for franchisees
          if (isFranchisee && !licenseAgreed) {
            // If franchisee has not agreed to license, set as unauthenticated
            this._authService.set_user_authenticated(false);
            this._router.navigate(['auth/login']);
          } else {
            // User is authenticated (either not franchisee or has agreed to license)
            this._authService.set_user_authenticated(true);

            // Continue with initialization
            this._menuRESTService.getMenu();
            this._fieldsService.getInstructorData();
            this._taxonomyService.initTaxonomyTerms();
            this._taxonomyOverridesService.refresh();
            this._packagesService.fetchPackages();

            // Check app version
            this._appVersionService.checkInitialVersion(response['app_version']).subscribe(
              () => {
                this._appVersionService.startPeriodicVersionCheck();
              },
              error => {
                LoggerService.error("Error checking app version:", error);
                this._appVersionService.startPeriodicVersionCheck();
              }
            );
          }

          resolve();
        } else {
          // Handle unauthenticated user
          this._authService.set_user_authenticated(false);
          // Auth pages (login, forgot/reset password) serve logged-out users,
          // don't hijack a direct visit such as the emailed password reset
          // link. The router hasn't resolved the initial navigation yet during
          // APP_INITIALIZER, so check the requested location instead.
          if (!window.location.pathname.startsWith('/auth/')) {
            this._router.navigate(['auth/login']);
          }
          resolve(); // or reject("User is not authenticated");
        }
      }, error => {
        LoggerService.error("Error during initialization:", error);
        reject(error);
      });
    });
  }
}
